import React, { useRef, useState } from "react";
import { CalendarIcon } from "@icons";
import { toInputFormats } from "./DatePicker.lib";
import { commitDateInputValue } from "./commitDateInputValue";
import styles from "./DatePicker.module.scss";

export const DatePickerPanelInput = <TValue,>({
    inputId,
    label,
    formattedValue,
    editableValue,
    placeholder,
    disabled,
    inputFormat,
    parseValue,
    emptyValue,
    onValueChange,
}: {
    inputId: string;
    label?: string;
    formattedValue: string;
    editableValue: string;
    placeholder: string;
    disabled: boolean;
    inputFormat: string | readonly string[];
    parseValue: (value: string) => TValue | undefined;
    emptyValue: TValue;
    onValueChange: (value: TValue) => void;
}) => {
    const expectedFormatHint = toInputFormats(inputFormat).join(" or ");
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const isCommittedRef = useRef(false);
    const commit = () =>
        commitDateInputValue({
            inputValue,
            parseValue,
            emptyValue,
            onValueChange,
        });

    return (
        <div className={styles.panelInputContainer}>
            {label && <label htmlFor={`${inputId}-panel`}>{label}</label>}
            <div className={styles.panelInput}>
                <CalendarIcon />
                <input
                    id={`${inputId}-panel`}
                    value={isEditing ? inputValue : formattedValue}
                    placeholder={placeholder}
                    disabled={disabled}
                    onFocus={() => {
                        setInputValue(editableValue);
                        setIsEditing(true);
                    }}
                    onChange={(event) => setInputValue(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.nativeEvent.isComposing) {
                            event.stopPropagation();
                            return;
                        }
                        if (event.key !== "Enter") {
                            return;
                        }
                        // Commits while focus is still inside the picker. The
                        // flag goes first: a listener that moves focus blurs
                        // the input inside `commit()`.
                        isCommittedRef.current = true;
                        commit();
                        event.currentTarget.blur();
                    }}
                    onBlur={() => {
                        if (!isCommittedRef.current) {
                            commit();
                        }
                        isCommittedRef.current = false;
                        setIsEditing(false);
                    }}
                    aria-describedby={`${inputId}-format`}
                />
                <span id={`${inputId}-format`} className={styles.srOnly}>
                    Expected format: {expectedFormatHint}
                </span>
            </div>
        </div>
    );
};
